import { useState, useEffect } from "react";
import { toast } from "@/components/ui/use-toast";
import GiftCardForm from "@/components/gift/GiftCardForm";
import GiftCardSteps from "@/components/gift/GiftCardSteps";
import GiftCardInfo from "@/components/gift/GiftCardInfo";
import GiftCardInfoModal from "@/components/gift/GiftCardInfoModal";
import GiftCardDuplicateWarningModal from "@/components/gift/GiftCardDuplicateWarningModal";
import { supabase } from "@/integrations/supabase/client";
import { ServiceItem } from "@/types";
import usePageSEO from "@/hooks/usePageSEO";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { useConsentRequired } from "@/hooks/useConsentRequired";
import { useSupabaseHealth } from "@/hooks/useSupabaseHealth";
import bonCadeauTemplate from "@/assets/bon-cadeau-template.png";

const GiftCard = () => {
  usePageSEO({
    title: "Bon Cadeau & Carte Cadeau Beauté Bio | Bio Beauté Zen Montpellier",
    description: "Offrez un bon cadeau ou une carte cadeau beauté bio à Montpellier. Soins visage, massages, épilations - envoi immédiat par email. Institut Bio Beauté Zen.",
    canonical: "https://biobeautezen.com/bon-cadeaux-estheticienne-bio-montpellier",
    ogTitle: "Bon Cadeau Bio Beauté Zen | Offrez du bien-être",
    ogDescription: "Offrez un bon cadeau beauté bio à Montpellier. Soins visage, massages, épilations - envoi immédiat par email."
  });
  
  const { trackEvent, trackConversion } = useGoogleAnalytics();
  
  // Demander le consentement des cookies pour cette page
  useConsentRequired();
  
  const { isRestricted } = useSupabaseHealth();
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [formData, setFormData] = useState({
    senderFirstName: "",
    senderLastName: "",
    senderEmail: "",
    senderPhone: "",
    recipientFirstName: "",
    recipientLastName: "",
    recipientEmail: "",
    recipientPhone: "",
    message: "",
    selectedServices: [] as string[],
    customAmount: "",
    giftType: "service" as "service" | "amount"
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  
  // États pour la modale d'information
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalMode, setInfoModalMode] = useState<"info" | "confirmation">("info");
  const [pendingCheckout, setPendingCheckout] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data, error } = await supabase
          .from("services")
          .select("*")
          .order("name");

        if (error) throw error;
        if (data) setServices(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des services:", error);
      }
    };

    fetchServices();
  }, []);

  const handleFormChange = (newFormData: any) => {
    setFormData(newFormData);
    
    // Track gift_card_started une seule fois quand l'utilisateur commence à remplir
    if (!hasTrackedStart) {
      const hasStarted = 
        newFormData.senderFirstName || 
        newFormData.senderEmail || 
        newFormData.selectedServices.length > 0 ||
        newFormData.customAmount;
        
      if (hasStarted) {
        trackEvent("gift_card_started");
        setHasTrackedStart(true);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isRestricted) {
      toast({
        title: "Service temporairement indisponible",
        description: "Les commandes en ligne sont momentanément hors service. Merci de me contacter directement par téléphone ou email.",
        variant: "destructive"
      });
      return;
    }
    
    // Validation côté client avant d'afficher la modale
    if (formData.giftType === "service" && (!formData.selectedServices || formData.selectedServices.length === 0)) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner au moins un service.",
        variant: "destructive"
      });
      return;
    }
    
    if (formData.giftType === "amount" && (!formData.customAmount || parseFloat(formData.customAmount) <= 0)) {
      toast({
        title: "Erreur",
        description: "Veuillez saisir un montant valide.",
        variant: "destructive"
      });
      return;
    }
    
    // Afficher la modale de confirmation avant de procéder au paiement
    setInfoModalMode("confirmation");
    setShowInfoModal(true);
    setPendingCheckout(true);
  };

  const handleConfirmAndProceed = async (confirmDuplicate = false) => {
    setShowInfoModal(false);
    setShowDuplicateWarning(false);
    setIsLoading(true);
    
    try {
      console.log("🎁 Création de la session Stripe Checkout", { confirmDuplicate });
      
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
        "create-gift-card-checkout",
        {
          body: {
            formData: formData,
            confirmDuplicate,
          }
        }
      );
      
      if (checkoutError) {
        console.error("❌ Erreur lors de la création de la session:", checkoutError);
        throw new Error(checkoutError.message);
      }

      // Doublon détecté : afficher l'avertissement
      if (checkoutData?.duplicateWarning) {
        console.log("⚠️ Doublon détecté, affichage de l'avertissement");
        setIsLoading(false);
        setShowDuplicateWarning(true);
        return;
      }
      
      if (checkoutError) {
        console.error("❌ Erreur lors de la création de la session:", checkoutError);
        throw new Error(checkoutError.message);
      }
      
      if (!checkoutData || !checkoutData.url) {
        throw new Error("Impossible de créer la session de paiement. Veuillez réessayer.");
      }
      
      console.log("✅ Session Stripe créée, redirection vers:", checkoutData.url.substring(0, 50) + "...");
      
      // Calculer le montant total pour le tracking
      let totalAmount = 0;
      if (formData.giftType === "amount") {
        totalAmount = parseFloat(formData.customAmount);
      } else {
        // Calculer le total des services sélectionnés
        totalAmount = formData.selectedServices.reduce((sum, serviceId) => {
          const service = services.find(s => s.id === serviceId);
          if (service) {
            const priceMatch = service.price.match(/(\d+)/);
            return sum + (priceMatch ? parseInt(priceMatch[1]) : 0);
          }
          return sum;
        }, 0);
      }
      
      trackConversion("gift_card_completed", totalAmount);
      
      // Rediriger vers Stripe Checkout
      window.location.href = checkoutData.url;
      
    } catch (error: any) {
      console.error("❌ Erreur:", error);
      
      const errorMessage = error.message || "Une erreur est survenue lors de la création du paiement.";
      
      toast({
        title: "Erreur",
        description: errorMessage,
        variant: "destructive"
      });
      
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('gift-card-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Accueil",
                "item": "https://biobeautezen.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Bon Cadeau",
                "item": "https://biobeautezen.com/bon-cadeaux-estheticienne-bio-montpellier"
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Carte Cadeau Beauté Bio",
            "alternateName": "Bon Cadeau Bio Beauté Zen",
            "description": "Carte cadeau ou bon cadeau pour soins beauté bio à Montpellier. Soins visage, massages, épilations.",
            "brand": {
              "@type": "Brand",
              "name": "Bio Beauté Zen"
            },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "EUR",
              "lowPrice": "10",
              "highPrice": "500",
              "availability": "https://schema.org/InStock"
            }
          })}
        </script>
      </Helmet>
      <div className="pt-20 pb-2">
        <div className="container mx-auto px-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Bon Cadeau</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      
      <main className="pb-12" id="gift-card-page">
        <section className="bg-bbz-beige/40 py-10 md:py-12" aria-labelledby="gift-card-hero">
          <div className="container mx-auto px-4 text-center">
            <h1 id="gift-card-hero" className="font-playfair text-4xl md:text-5xl font-semibold text-bbz-text mb-6">
              Bons Cadeaux
            </h1>
            <p className="sr-only">
              Carte cadeau beauté Montpellier - Bon cadeau institut de beauté bio - Idée cadeau soins esthétiques
            </p>
            <p className="text-lg md:text-xl max-w-3xl mx-auto mb-8">
              Offrez un moment de bien-être à vos proches
            </p>
            
            <div className="flex justify-center my-12">
              <img 
                src={bonCadeauTemplate} 
                alt="Exemple de bon cadeau Bio Beauté Zen" 
                className="h-32 w-auto sm:h-40 md:h-48 lg:h-56 max-w-full rounded-lg rotate-2 shadow-2xl drop-shadow-lg object-contain transition-transform duration-700 ease-in-out hover:scale-105 hover:-rotate-1 transform-gpu" 
              />
            </div>
            
            <Button 
              onClick={scrollToForm}
              size="lg"
              className="gap-2"
            >
              Accéder au formulaire
              <ChevronDown className="w-4 h-4" />
            </Button>
          </div>
        </section>

        <GiftCardSteps />

        <GiftCardInfo />

        <section className="section-container-compact" id="gift-card-form">
          <div className="max-w-2xl mx-auto">
            <GiftCardForm 
              onSubmit={handleSubmit} 
              onChange={handleFormChange} 
              formData={formData} 
              isLoading={isLoading} 
            />
          </div>
        </section>
        
        <GiftCardInfoModal
          open={showInfoModal}
          onOpenChange={setShowInfoModal}
          mode={infoModalMode}
          onConfirm={infoModalMode === "confirmation" ? () => handleConfirmAndProceed(false) : undefined}
        />

        <GiftCardDuplicateWarningModal
          open={showDuplicateWarning}
          onOpenChange={setShowDuplicateWarning}
          onConfirm={() => handleConfirmAndProceed(true)}
          isLoading={isLoading}
        />
      </main>
    </>
  );
};

export default GiftCard;
